import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.role === "super_admin" || user.role === "admin") navigate("/admin");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("toast.login_failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block" data-testid="login-logo"><img src="/logo.jpg" alt="Smartpriced" className="h-12 mx-auto" /></Link>
        </div>
        <Card className="shadow-xl shadow-slate-200/50 border-slate-100">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl">{t("nav.login")}</CardTitle>
            <CardDescription>{t("login.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("form.email")}</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="je@email.com" required className="mt-1.5" data-testid="login-email-input" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">{t("form.password")}</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("form.password")} required className="mt-1.5" data-testid="login-password-input" />
              </div>
              <Button type="submit" className="w-full bg-[#2CA8AC] hover:bg-[#248F92] h-11" disabled={loading} data-testid="login-submit-button">
                {loading ? t("admin.loading") : <><LogIn className="w-4 h-4 mr-2" />{t("nav.login")}</>}
              </Button>
              <div className="text-center mt-3">
                <Link to="/reset-password" className="text-xs text-[#2CA8AC] hover:text-[#248F92]" data-testid="forgot-password-link">{t("login.forgot")}</Link>
              </div>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              {t("login.no_account")} <Link to="/register" className="text-[#2CA8AC] hover:text-[#248F92] font-medium" data-testid="login-register-link">{t("nav.register")}</Link>
            </p>
          </CardContent>
        </Card>
        <div className="mt-6 text-center space-y-2">
          <Link to="/" className="text-sm text-[#2CA8AC] hover:text-[#248F92] font-medium" data-testid="login-home-link">{t("register.back_home")}</Link>
        </div>
      </div>
    </div>
  );
}
